import { TimeSeriesInterval, TimeSeriesRelativeDuration } from "@glide/component-utils";
import type { GlideDateTimeDocumentData, GlideDateTimeZone } from "@glide/data-types";
import { GlideDateTime, GlideJSON, isGlideDateTimeDocumentData } from "@glide/data-types";
import type { DataPlotRowData } from "@glide/fluent-components/dist/js/base-components";
import { DataPlotRollupKind } from "@glide/fluent-components/dist/js/fluent-components";
import { assertNever, hasOwnProperty } from "@glideapps/ts-necessities";
import {
    eachDayOfInterval,
    eachMonthOfInterval,
    eachHourOfInterval,
    eachQuarterOfInterval,
    endOfHour,
    startOfHour,
    endOfDay,
    startOfDay,
    endOfMonth,
    endOfQuarter,
    startOfMonth,
    startOfQuarter,
    isSameYear,
    startOfWeek,
    endOfWeek,
    eachWeekOfInterval,
    eachYearOfInterval,
    startOfYear,
    endOfYear,
    addMilliseconds,
} from "date-fns";
type TimeBucket = {
    start: Date;
    end: Date;
    name: string; // Formatted name for display
};

// FIXME: The rhs of the conditional these get passed to are somehow
// observed by the query, so something needs to be done to prevent
// re-fetching that is better than moving this here for use on initial load.
const now = new Date();

/**
 * Calculate the optimal time series interval based on a date range in milliseconds
 */
export function getIntervalFromDateRange(startTimestamp: number, endTimestamp: number): TimeSeriesInterval {
    const diff = endTimestamp - startTimestamp;
    const diffInDays = diff / (1000 * 60 * 60 * 24);

    if (diffInDays < 1) {
        return TimeSeriesInterval.Hour;
    }
    if (diffInDays < 31) {
        return TimeSeriesInterval.Day;
    }
    if (diffInDays < 90) {
        return TimeSeriesInterval.Week;
    }
    if (diffInDays < 365) {
        return TimeSeriesInterval.Month;
    }
    return TimeSeriesInterval.Year;
}

// Timezone-aware boundary helpers
const HOUR_IN_MS = 60 * 60 * 1000;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

function startOfUTCHour(date: Date): Date {
    return new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), 0, 0, 0)
    );
}

function endOfUTCHour(date: Date): Date {
    return addMilliseconds(startOfUTCHour(addMilliseconds(date, HOUR_IN_MS)), -1);
}

function startOfUTCDay(date: Date): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
}

function endOfUTCDay(date: Date): Date {
    return addMilliseconds(startOfUTCDay(addMilliseconds(date, DAY_IN_MS)), -1);
}

function startOfUTCWeek(date: Date): Date {
    const dayOfWeek = date.getUTCDay();
    const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday as start of week
    const startDate = new Date(date);
    startDate.setUTCDate(date.getUTCDate() - diff);
    return startOfUTCDay(startDate);
}

function endOfUTCWeek(date: Date): Date {
    const start = startOfUTCWeek(date);
    const end = new Date(start);
    end.setUTCDate(start.getUTCDate() + 6);
    return endOfUTCDay(end);
}

function startOfUTCMonth(date: Date): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0));
}

function endOfUTCMonth(date: Date): Date {
    return addMilliseconds(startOfUTCMonth(addMilliseconds(date, 32 * DAY_IN_MS)), -1);
}

function startOfUTCQuarter(date: Date): Date {
    const quarterMonth = Math.floor(date.getUTCMonth() / 3) * 3;
    return new Date(Date.UTC(date.getUTCFullYear(), quarterMonth, 1, 0, 0, 0, 0));
}

function endOfUTCQuarter(date: Date): Date {
    const start = startOfUTCQuarter(date);
    const nextQuarter = new Date(start);
    nextQuarter.setUTCMonth(start.getUTCMonth() + 3);
    return addMilliseconds(nextQuarter, -1);
}

function startOfUTCYear(date: Date): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), 0, 1, 0, 0, 0, 0));
}

function endOfUTCYear(date: Date): Date {
    return addMilliseconds(new Date(Date.UTC(date.getUTCFullYear() + 1, 0, 1, 0, 0, 0, 0)), -1);
}

export function getDefaultIntervalFromDuration(
    duration: TimeSeriesRelativeDuration | "All" | TimeSeriesRangeGlideJSON
): TimeSeriesInterval {
    switch (duration) {
        case TimeSeriesRelativeDuration.Last7Days:
            return TimeSeriesInterval.Day;
        case TimeSeriesRelativeDuration.Last30Days:
            return TimeSeriesInterval.Day;
        case TimeSeriesRelativeDuration.Last90Days:
            return TimeSeriesInterval.Week;
        case TimeSeriesRelativeDuration.Last12Months:
            return TimeSeriesInterval.Month;
        case TimeSeriesRelativeDuration.Last24Hours:
            return TimeSeriesInterval.Hour;
        case TimeSeriesRelativeDuration.Yesterday:
            return TimeSeriesInterval.Hour;
        case TimeSeriesRelativeDuration.LastWeek:
            return TimeSeriesInterval.Day;
        case TimeSeriesRelativeDuration.LastMonth:
            return TimeSeriesInterval.Day;
        case TimeSeriesRelativeDuration.LastQuarter:
            return TimeSeriesInterval.Week;
        case TimeSeriesRelativeDuration.LastYear:
            return TimeSeriesInterval.Month;
        case TimeSeriesRelativeDuration.DayToNow:
            return TimeSeriesInterval.Hour;
        case TimeSeriesRelativeDuration.WeekToDate:
            return TimeSeriesInterval.Day;
        case TimeSeriesRelativeDuration.MonthToDate:
            return TimeSeriesInterval.Day;
        case TimeSeriesRelativeDuration.QuarterToDate:
            return TimeSeriesInterval.Week;
        case TimeSeriesRelativeDuration.YearToDate:
            return TimeSeriesInterval.Month;
        case "All":
            // For "All", we return a default but the actual interval should be calculated
            // later when we have access to the actual data range
            return TimeSeriesInterval.Day;
        default:
            if (duration instanceof GlideJSON && isTimeSeriesRange(duration.jsonValue)) {
                const { start, end } = convertTimeSeriesRangeToDate(duration.jsonValue);
                return getIntervalFromDateRange(start.getTime(), end.getTime());
            }
            return TimeSeriesInterval.Day;
    }
}

function formatBucketLabel(
    interval: TimeSeriesInterval,
    date: Date,
    timeZone: GlideDateTimeZone,
    forceDay: boolean = false,
    forceMonth: boolean = false,
    forceYear: boolean = false
): string {
    // When timeZone is "agnostic", use UTC to ignore local timezone
    // When timeZone is "local", use undefined to respect user's timezone
    const tzOption = timeZone === "agnostic" ? "UTC" : undefined;

    switch (interval) {
        case TimeSeriesInterval.Hour:
            return date.toLocaleString(undefined, {
                hour: "numeric",
                minute: "numeric",
                day: forceDay ? "numeric" : undefined,
                month: forceMonth ? "short" : undefined,
                year: forceYear ? "numeric" : undefined,
                timeZone: tzOption,
            });
        case TimeSeriesInterval.Day:
            return date.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: forceYear ? "numeric" : undefined,
                timeZone: tzOption,
            });
        case TimeSeriesInterval.Week:
            return date.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: forceYear ? "numeric" : undefined,
                timeZone: tzOption,
            });
        case TimeSeriesInterval.Month:
            return date.toLocaleDateString(undefined, { month: "short", year: "numeric", timeZone: tzOption });
        case TimeSeriesInterval.Quarter: {
            // For quarters, we need to get the month in the appropriate timezone
            const monthGetter = timeZone === "agnostic" ? date.getUTCMonth() : date.getMonth();
            const yearGetter = timeZone === "agnostic" ? date.getUTCFullYear() : date.getFullYear();
            const quarter = Math.floor(monthGetter / 3) + 1;
            return `Q${quarter} ${yearGetter}`;
        }
        case TimeSeriesInterval.Year:
            return (timeZone === "agnostic" ? date.getUTCFullYear() : date.getFullYear()).toString();
        default:
            assertNever(interval);
    }
}

export type TimeSeriesRangeGlideJSON = GlideJSON;

export type TimeSeriesRange = {
    start: GlideDateTime | GlideDateTimeDocumentData;
    end: GlideDateTime | GlideDateTimeDocumentData;
};

export function isTimeSeriesRange(obj: unknown): obj is TimeSeriesRange {
    if (!hasOwnProperty(obj, "start") || !hasOwnProperty(obj, "end")) {
        return false;
    }

    const startIsValid = obj.start instanceof GlideDateTime || isGlideDateTimeDocumentData(obj.start);
    const endIsValid = obj.end instanceof GlideDateTime || isGlideDateTimeDocumentData(obj.end);

    return startIsValid && endIsValid;
}

export function convertTimeSeriesRangeToDate(range: TimeSeriesRange): { start: Date; end: Date } {
    const start =
        range.start instanceof GlideDateTime
            ? range.start.asOriginTimeZoneAgnosticDate()
            : GlideDateTime.fromDocumentData(range.start).asOriginTimeZoneAgnosticDate();
    const end =
        range.end instanceof GlideDateTime
            ? range.end.asOriginTimeZoneAgnosticDate()
            : GlideDateTime.fromDocumentData(range.end).asOriginTimeZoneAgnosticDate();

    return { start, end };
}

function getFormattedCustomRangeLabel(start: Date, end: Date): string {
    if (isSameYear(end, start)) {
        return `${start.toLocaleDateString(navigator.language, {
            day: "2-digit",
            month: "2-digit",
        })} - ${end.toLocaleDateString(navigator.language, {
            day: "2-digit",
            month: "2-digit",
        })}`;
    }

    return `${start.toLocaleDateString(navigator.language, {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
    })} - ${end.toLocaleDateString(navigator.language, { day: "2-digit", month: "2-digit", year: "2-digit" })}`;
}

export function convertTimeDurationToBucket(
    duration: TimeSeriesRelativeDuration | TimeSeriesRangeGlideJSON
): TimeBucket {
    if (duration instanceof GlideJSON) {
        if (isTimeSeriesRange(duration.jsonValue)) {
            const { start, end } = convertTimeSeriesRangeToDate(duration.jsonValue);
            return {
                start,
                end,
                name: getFormattedCustomRangeLabel(start, end),
            };
        }
        return {
            start: new Date(),
            end: new Date(),
            name: "n/a",
        };
    }

    switch (duration) {
        case TimeSeriesRelativeDuration.Last7Days: {
            const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return {
                start: start,
                end: now,
                name: `Last 7 days`,
            };
        }
        case TimeSeriesRelativeDuration.Last30Days: {
            const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return {
                start: start,
                end: now,
                name: `Last 30 days`,
            };
        }
        case TimeSeriesRelativeDuration.Last90Days: {
            const start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            return {
                start: start,
                end: now,
                name: `Last 90 days`,
            };
        }
        case TimeSeriesRelativeDuration.Last12Months: {
            const start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
            return {
                start: start,
                end: now,
                name: `Last 12 months`,
            };
        }
        case TimeSeriesRelativeDuration.Last24Hours: {
            const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            return {
                start: start,
                end: now,
                name: `Last 24 hours`,
            };
        }
        case TimeSeriesRelativeDuration.Yesterday: {
            const yesterday = new Date(now);
            yesterday.setDate(now.getDate() - 1);
            const start = startOfDay(yesterday);
            const end = endOfDay(yesterday);
            return {
                start,
                end,
                name: `Yesterday`,
            };
        }
        case TimeSeriesRelativeDuration.LastWeek: {
            const previousWeek = new Date(now);
            previousWeek.setDate(now.getDate() - 7);
            return {
                start: startOfWeek(previousWeek),
                end: endOfWeek(previousWeek),
                name: `Last week`,
            };
        }
        case TimeSeriesRelativeDuration.LastMonth: {
            const lastMonthDate = new Date(now);
            lastMonthDate.setDate(1);
            lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
            return {
                start: startOfMonth(lastMonthDate),
                end: endOfMonth(lastMonthDate),
                name: `Last month`,
            };
        }
        case TimeSeriesRelativeDuration.LastQuarter: {
            const currentQuarter = Math.floor(now.getMonth() / 3);
            const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
            const lastQuarterYear = currentQuarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
            const lastQuarterDate = new Date(lastQuarterYear, lastQuarter * 3, 1);
            return {
                start: startOfQuarter(lastQuarterDate),
                end: endOfQuarter(lastQuarterDate),
                name: `Last quarter`,
            };
        }
        case TimeSeriesRelativeDuration.LastYear: {
            const lastYearDate = new Date(now);
            lastYearDate.setFullYear(lastYearDate.getFullYear() - 1);
            return {
                start: startOfYear(lastYearDate),
                end: endOfYear(lastYearDate),
                name: `Last year`,
            };
        }
        case TimeSeriesRelativeDuration.DayToNow: {
            const today = new Date(now);
            return {
                start: startOfDay(today),
                end: now,
                name: `Day to now`,
            };
        }
        case TimeSeriesRelativeDuration.WeekToDate: {
            const weekStart = startOfWeek(now);
            return {
                start: weekStart,
                end: now,
                name: `Week to date`,
            };
        }
        case TimeSeriesRelativeDuration.MonthToDate: {
            const monthStart = startOfMonth(now);
            return {
                start: monthStart,
                end: now,
                name: `Month to date`,
            };
        }
        case TimeSeriesRelativeDuration.QuarterToDate: {
            const quarterStart = startOfQuarter(now);
            return {
                start: quarterStart,
                end: now,
                name: `Quarter to date`,
            };
        }
        case TimeSeriesRelativeDuration.YearToDate: {
            const yearStart = startOfYear(now);
            return {
                start: yearStart,
                end: now,
                name: `Year to date`,
            };
        }
        default:
            assertNever(duration);
    }
}

export function makeTimeBucketsFromDurationAndInterval(
    duration: TimeSeriesRelativeDuration | TimeSeriesRangeGlideJSON | "All",
    interval: TimeSeriesInterval,
    minTimestamp: number,
    maxTimestamp: number,
    timeZone: GlideDateTimeZone
): TimeBucket[] {
    const { start, end } =
        duration === "All" ? { start: minTimestamp, end: maxTimestamp } : convertTimeDurationToBucket(duration);

    // Choose appropriate date functions based on timezone
    const isUTC = timeZone === "agnostic";

    const getYear = (date: Date) => (isUTC ? date.getUTCFullYear() : date.getFullYear());
    const getMonth = (date: Date) => (isUTC ? date.getUTCMonth() : date.getMonth());
    const getDay = (date: Date) => (isUTC ? date.getUTCDay() : date.getDay());

    // Select timezone-appropriate boundary functions
    const startOfHourFn = isUTC ? startOfUTCHour : startOfHour;
    const endOfHourFn = isUTC ? endOfUTCHour : endOfHour;
    const startOfDayFn = isUTC ? startOfUTCDay : startOfDay;
    const endOfDayFn = isUTC ? endOfUTCDay : endOfDay;
    const startOfWeekFn = isUTC ? startOfUTCWeek : startOfWeek;
    const endOfWeekFn = isUTC ? endOfUTCWeek : endOfWeek;
    const startOfMonthFn = isUTC ? startOfUTCMonth : startOfMonth;
    const endOfMonthFn = isUTC ? endOfUTCMonth : endOfMonth;
    const startOfQuarterFn = isUTC ? startOfUTCQuarter : startOfQuarter;
    const endOfQuarterFn = isUTC ? endOfUTCQuarter : endOfQuarter;
    const startOfYearFn = isUTC ? startOfUTCYear : startOfYear;
    const endOfYearFn = isUTC ? endOfUTCYear : endOfYear;

    const forceYear = getYear(new Date(start)) !== getYear(new Date(end));
    const forceMonth = forceYear || getMonth(new Date(start)) !== getMonth(new Date(end));
    const forceDay = forceMonth || getDay(new Date(start)) !== getDay(new Date(end));

    let buckets: TimeBucket[] = [];

    switch (interval) {
        case TimeSeriesInterval.Hour:
            buckets = eachHourOfInterval({ start, end }).map(date => ({
                start: startOfHourFn(date),
                end: endOfHourFn(date),
                name: formatBucketLabel(interval, date, timeZone, forceDay, forceMonth, forceYear),
            }));
            break;
        case TimeSeriesInterval.Day:
            buckets = eachDayOfInterval({ start, end }).map(date => ({
                start: startOfDayFn(date),
                end: endOfDayFn(date),
                name: formatBucketLabel(interval, date, timeZone, forceDay, forceMonth, forceYear),
            }));
            break;
        case TimeSeriesInterval.Week:
            buckets = eachWeekOfInterval({ start, end }).map(date => ({
                start: startOfWeekFn(date),
                end: endOfWeekFn(date),
                name: formatBucketLabel(interval, date, timeZone, forceDay, forceMonth, forceYear),
            }));
            break;
        case TimeSeriesInterval.Month:
            buckets = eachMonthOfInterval({ start, end }).map(date => ({
                start: startOfMonthFn(date),
                end: endOfMonthFn(date),
                name: formatBucketLabel(interval, date, timeZone, forceDay, forceMonth, forceYear),
            }));
            break;
        case TimeSeriesInterval.Quarter:
            buckets = eachQuarterOfInterval({ start, end }).map(date => ({
                start: startOfQuarterFn(date),
                end: endOfQuarterFn(date),
                name: formatBucketLabel(interval, date, timeZone),
            }));
            break;
        case TimeSeriesInterval.Year:
            buckets = eachYearOfInterval({ start, end }).map(date => ({
                start: startOfYearFn(date),
                end: endOfYearFn(date),
                name: formatBucketLabel(interval, date, timeZone),
            }));
            break;
    }

    return buckets;
}

export function createTimeSeriesAggregationInBuckets(
    buckets: TimeBucket[],
    data: DataPlotRowData[],
    aggregationsMap: Map<string, DataPlotRollupKind | undefined>
): DataPlotRowData[] {
    const results: DataPlotRowData[] = buckets.map(bucket => ({
        name: bucket.name,
        points: {}, // Will be populated with aggregated values
    }));

    for (const row of data) {
        // Find which bucket this data point belongs to
        let bucketIndex = -1;
        const timestamp = new Date(row.timestamp ?? row.name).getTime();

        for (let i = 0; i < buckets.length; i++) {
            const bucket = buckets[i];
            if (timestamp >= bucket.start.getTime() && timestamp < bucket.end.getTime()) {
                bucketIndex = i;
                break;
            }
        }

        // No bucket
        if (bucketIndex === -1) continue;

        // Aggregating the values in data points
        const result = results[bucketIndex];
        for (const key in row.points) {
            if (Object.prototype.hasOwnProperty.call(row.points, key)) {
                // Initialize if this key hasn't been seen before
                if (result.points[key] === undefined) {
                    result.points[key] = {
                        value: 0,
                        color: row.points[key].color,
                    };
                    // pass an undefined current value to avoid the min/max zero bug.
                    result.points[key].value = applyAggregation(aggregationsMap, key, undefined, row.points[key].value);
                } else {
                    result.points[key].value = applyAggregation(
                        aggregationsMap,
                        key,
                        result.points[key].value,
                        row.points[key].value
                    );
                }
            }
        }
    }

    return results;
}

function applyAggregation(
    aggregationsMap: Map<string, DataPlotRollupKind | undefined>,
    dataKey: string,
    currentValue: number | undefined,
    nextValue: number
): number {
    const aggregateFunction = aggregationsMap.get(dataKey);
    // case where first point doesn't exist yet (min/max bug)
    if (currentValue === undefined) {
        return nextValue;
    }
    // default if there is no aggregation function specified
    if (aggregateFunction === undefined) {
        return currentValue + nextValue;
    }
    switch (aggregateFunction) {
        case DataPlotRollupKind.Sum:
            return currentValue + nextValue;
        case DataPlotRollupKind.Minimum:
            return Math.min(currentValue, nextValue);
        case DataPlotRollupKind.Maximum:
            return Math.max(currentValue, nextValue);
        case DataPlotRollupKind.Average:
            return (currentValue + nextValue) / 2;
        case DataPlotRollupKind.CountNonEmpty:
            return currentValue + 1;
        default:
            assertNever(aggregateFunction);
    }
}
